import { z } from "zod";
import { LocalDateSchema, PlanningOpaqueIdSchema, PlanningRequestIdSchema } from "./planning";

const IsoTimestampSchema = z.iso.datetime({ offset: true });
const FilterTextSchema = z.string().trim().min(1).max(120);

function hasUniqueValues(values: readonly string[]): boolean {
  return new Set(values).size === values.length;
}

function hasAscendingOptionalWindow(
  from: string | null,
  to: string | null,
  context: z.RefinementCtx,
  field: string,
): void {
  if (from !== null && to !== null && from > to) {
    context.addIssue({
      code: "custom",
      message: `${field} start must not be after its end`,
      path: [field],
    });
  }
}

export const SearchDocumentTypeSchema = z.enum(["task", "routine", "project", "note"]);

export const SearchDocumentSubtypeSchema = z.enum([
  "task",
  "routine",
  "project",
  "evidence",
  "review",
]);

export const ProductHistoryActorTypeSchema = z.enum(["adult", "child", "system", "agent"]);
export const ProductHistoryClientTypeSchema = z.enum(["web", "pwa", "native", "mcp", "system"]);
export const SecurityAuditActorTypeSchema = z.enum(["adult", "child", "system"]);
export const SecurityAuditOutcomeSchema = z.enum(["success", "failure", "denied"]);

/**
 * The API signs and filter-binds cursors. Clients must treat this value as an
 * opaque token and must never construct or alter its payload.
 */
export const SearchHistoryCursorSchema = z
  .string()
  .min(16)
  .max(4096)
  .regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/u);

const DocumentTypeFilterSchema = z
  .array(SearchDocumentTypeSchema)
  .min(1)
  .max(4)
  .refine(hasUniqueValues, "Document types must be unique.");

const OptionalDocumentTypeFilterSchema = z
  .array(SearchDocumentTypeSchema)
  .max(4)
  .refine(hasUniqueValues, "Resource types must be unique.");

const TextListFilterSchema = z
  .array(FilterTextSchema)
  .max(30)
  .refine(hasUniqueValues, "Filter values must be unique.");

export const SearchWorkspaceContentQuerySchema = z
  .object({
    query: z.string().trim().min(1).max(200).nullable().default(null),
    document_types: DocumentTypeFilterSchema.default(["task", "routine", "project", "note"]),
    child_id: PlanningOpaqueIdSchema.nullable().default(null),
    statuses: TextListFilterSchema.default([]),
    area_id: PlanningOpaqueIdSchema.nullable().default(null),
    project_id: PlanningOpaqueIdSchema.nullable().default(null),
    routine_id: PlanningOpaqueIdSchema.nullable().default(null),
    has_evidence: z.boolean().nullable().default(null),
    local_date_from: LocalDateSchema.nullable().default(null),
    local_date_to: LocalDateSchema.nullable().default(null),
    limit: z.number().int().min(1).max(50).default(50),
    cursor: SearchHistoryCursorSchema.nullable().default(null),
  })
  .strict()
  .superRefine((value, context) => {
    hasAscendingOptionalWindow(value.local_date_from, value.local_date_to, context, "local_date");
  });

export const SearchContentItemSchema = z
  .object({
    workspace_id: PlanningOpaqueIdSchema,
    document_type: SearchDocumentTypeSchema,
    document_subtype: SearchDocumentSubtypeSchema,
    document_id: PlanningOpaqueIdSchema,
    title: z.string().min(1).max(240),
    excerpt: z.string().max(320),
    status: FilterTextSchema,
    area_id: PlanningOpaqueIdSchema.nullable(),
    project_id: PlanningOpaqueIdSchema.nullable(),
    routine_id: PlanningOpaqueIdSchema.nullable(),
    has_evidence: z.boolean(),
    local_date: LocalDateSchema.nullable(),
    updated_at: IsoTimestampSchema,
  })
  .strict();

export const SearchContentPageResponseSchema = z
  .object({
    items: z.array(SearchContentItemSchema).max(50),
    next_cursor: SearchHistoryCursorSchema.nullable(),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const ProductHistoryQuerySchema = z
  .object({
    resource_types: OptionalDocumentTypeFilterSchema.default([]),
    event_types: TextListFilterSchema.default([]),
    actor_id: PlanningOpaqueIdSchema.nullable().default(null),
    created_from: IsoTimestampSchema.nullable().default(null),
    created_to: IsoTimestampSchema.nullable().default(null),
    limit: z.number().int().min(1).max(50).default(50),
    cursor: SearchHistoryCursorSchema.nullable().default(null),
  })
  .strict()
  .superRefine((value, context) => {
    hasAscendingOptionalWindow(value.created_from, value.created_to, context, "created_at");
  });

export const ProductHistoryItemSchema = z
  .object({
    workspace_id: PlanningOpaqueIdSchema,
    source_event_id: PlanningOpaqueIdSchema.nullable(),
    resource_type: SearchDocumentTypeSchema,
    resource_id: PlanningOpaqueIdSchema,
    resource_title: z.string().min(1).max(240),
    event_type: FilterTextSchema,
    actor_type: ProductHistoryActorTypeSchema.nullable(),
    actor_id: PlanningOpaqueIdSchema.nullable(),
    client_type: ProductHistoryClientTypeSchema.nullable(),
    details: z.record(z.string(), z.unknown()),
    created_at: IsoTimestampSchema,
  })
  .strict();

export const ProductHistoryPageResponseSchema = z
  .object({
    items: z.array(ProductHistoryItemSchema).max(50),
    next_cursor: SearchHistoryCursorSchema.nullable(),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const SecurityAuditQuerySchema = z
  .object({
    event_types: TextListFilterSchema.default([]),
    actor_id: PlanningOpaqueIdSchema.nullable().default(null),
    actor_types: z
      .array(SecurityAuditActorTypeSchema)
      .max(3)
      .refine(hasUniqueValues, "Actor types must be unique.")
      .default([]),
    target_type: FilterTextSchema.nullable().default(null),
    target_id: PlanningOpaqueIdSchema.nullable().default(null),
    outcomes: z
      .array(SecurityAuditOutcomeSchema)
      .max(3)
      .refine(hasUniqueValues, "Outcomes must be unique.")
      .default([]),
    created_from: IsoTimestampSchema.nullable().default(null),
    created_to: IsoTimestampSchema.nullable().default(null),
    limit: z.number().int().min(1).max(50).default(50),
    cursor: SearchHistoryCursorSchema.nullable().default(null),
  })
  .strict()
  .superRefine((value, context) => {
    hasAscendingOptionalWindow(value.created_from, value.created_to, context, "created_at");
  });

/** Secret-free security projection. Audit summary/details are intentionally absent. */
export const SecurityAuditItemSchema = z
  .object({
    id: PlanningOpaqueIdSchema,
    workspace_id: PlanningOpaqueIdSchema,
    event_type: FilterTextSchema,
    actor_id: PlanningOpaqueIdSchema,
    actor_type: SecurityAuditActorTypeSchema,
    target_type: FilterTextSchema.nullable(),
    target_id: PlanningOpaqueIdSchema.nullable(),
    request_id: PlanningRequestIdSchema,
    outcome: SecurityAuditOutcomeSchema,
    created_at: IsoTimestampSchema,
  })
  .strict();

export const SecurityAuditPageResponseSchema = z
  .object({
    items: z.array(SecurityAuditItemSchema).max(50),
    next_cursor: SearchHistoryCursorSchema.nullable(),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export type SearchWorkspaceContentQuery = z.infer<typeof SearchWorkspaceContentQuerySchema>;
export type SearchContentItem = z.infer<typeof SearchContentItemSchema>;
export type SearchContentPageResponse = z.infer<typeof SearchContentPageResponseSchema>;
export type ProductHistoryQuery = z.infer<typeof ProductHistoryQuerySchema>;
export type ProductHistoryItem = z.infer<typeof ProductHistoryItemSchema>;
export type ProductHistoryPageResponse = z.infer<typeof ProductHistoryPageResponseSchema>;
export type SecurityAuditQuery = z.infer<typeof SecurityAuditQuerySchema>;
export type SecurityAuditItem = z.infer<typeof SecurityAuditItemSchema>;
export type SecurityAuditPageResponse = z.infer<typeof SecurityAuditPageResponseSchema>;

function appendQueryValue(
  search: URLSearchParams,
  name: string,
  value: string | number | boolean | null,
): void {
  if (value !== null) search.append(name, String(value));
}

function appendQueryValues(search: URLSearchParams, name: string, values: readonly string[]): void {
  for (const value of values) search.append(name, value);
}

/** Serialize a validated search contract without collapsing repeated filters. */
export function searchWorkspaceContentPath(
  workspaceId: string,
  input: SearchWorkspaceContentQuery,
): string {
  const search = new URLSearchParams();
  appendQueryValue(search, "query", input.query);
  appendQueryValues(search, "document_types", input.document_types);
  appendQueryValue(search, "child_id", input.child_id);
  appendQueryValues(search, "statuses", input.statuses);
  appendQueryValue(search, "area_id", input.area_id);
  appendQueryValue(search, "project_id", input.project_id);
  appendQueryValue(search, "routine_id", input.routine_id);
  appendQueryValue(search, "has_evidence", input.has_evidence);
  appendQueryValue(search, "local_date_from", input.local_date_from);
  appendQueryValue(search, "local_date_to", input.local_date_to);
  appendQueryValue(search, "limit", input.limit);
  appendQueryValue(search, "cursor", input.cursor);
  return `/api/v1/workspaces/${workspaceId}/search?${search.toString()}`;
}

/** Serialize a validated product-history contract with its opaque cursor intact. */
export function productHistoryPath(workspaceId: string, input: ProductHistoryQuery): string {
  const search = new URLSearchParams();
  appendQueryValues(search, "resource_types", input.resource_types);
  appendQueryValues(search, "event_types", input.event_types);
  appendQueryValue(search, "actor_id", input.actor_id);
  appendQueryValue(search, "created_from", input.created_from);
  appendQueryValue(search, "created_to", input.created_to);
  appendQueryValue(search, "limit", input.limit);
  appendQueryValue(search, "cursor", input.cursor);
  return `/api/v1/workspaces/${workspaceId}/history?${search.toString()}`;
}

/** Serialize a validated secret-free security-audit contract. */
export function securityAuditPath(workspaceId: string, input: SecurityAuditQuery): string {
  const search = new URLSearchParams();
  appendQueryValues(search, "event_types", input.event_types);
  appendQueryValue(search, "actor_id", input.actor_id);
  appendQueryValues(search, "actor_types", input.actor_types);
  appendQueryValue(search, "target_type", input.target_type);
  appendQueryValue(search, "target_id", input.target_id);
  appendQueryValues(search, "outcomes", input.outcomes);
  appendQueryValue(search, "created_from", input.created_from);
  appendQueryValue(search, "created_to", input.created_to);
  appendQueryValue(search, "limit", input.limit);
  appendQueryValue(search, "cursor", input.cursor);
  return `/api/v1/workspaces/${workspaceId}/security-audit?${search.toString()}`;
}
